import { createFileRoute } from '@tanstack/react-router';
import { Search, UserPlus } from 'lucide-react';
import { useState } from 'react';

import { QueryError } from '@/components/QueryError';
import { ChangeRoleDialog } from '@/components/team/ChangeRoleDialog';
import { CustomRolesList } from '@/components/team/CustomRolesList';
import { DeleteMemberDialog } from '@/components/team/DeleteMemberDialog';
import { InvitationsList } from '@/components/team/InvitationsList';
import { InviteMemberDialog } from '@/components/team/InviteMemberDialog';
import { MembersTable } from '@/components/team/MembersTable';
import { PermissionsMatrix } from '@/components/team/PermissionsMatrix';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ASSIGNABLE_ROLES, type AssignableRole } from '@/lib/can-manage-role';
import { useHasPermission } from '@/lib/permissions';
import { ROLE_LABELS } from '@/lib/role-display';
import { requirePermission } from '@/lib/route-guards';
import { MEMBER_STATUS_LABELS, useMembers, type Member, type MemberStatus } from '@/lib/users';

export const Route = createFileRoute('/_authed/team')({
  beforeLoad: () => requirePermission('users.read'),
  component: TeamPage,
});

const ALL = 'all';

export function TeamPage() {
  const canInvite = useHasPermission('users.invite');
  const canManageRoles = useHasPermission('users.changeRole');
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Équipe</h1>
        {canInvite && (
          <Button onClick={() => setInviteOpen(true)}><UserPlus className="h-4 w-4 mr-2" /> Inviter un membre</Button>
        )}
      </div>
      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">Membres</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
          {canManageRoles && <TabsTrigger value="custom-roles">Rôles personnalisés</TabsTrigger>}
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>
        <TabsContent value="members"><MembersTab /></TabsContent>
        <TabsContent value="invitations"><InvitationsList /></TabsContent>
        {canManageRoles && <TabsContent value="custom-roles"><CustomRolesList /></TabsContent>}
        <TabsContent value="permissions"><PermissionsMatrix /></TabsContent>
      </Tabs>
      <InviteMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

function MembersTab() {
  const [role, setRole] = useState<AssignableRole | typeof ALL>(ALL);
  const [status, setStatus] = useState<MemberStatus | typeof ALL>(ALL);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [roleTarget, setRoleTarget] = useState<Member | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Member | null>(null);

  // Tout changement de filtre ramène à la première page
  const filter = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

  const query = useMembers({
    page,
    role: role === ALL ? undefined : role,
    status: status === ALL ? undefined : status,
    search,
  });
  const { data, isLoading } = query;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Nom ou email…"
            aria-label="Rechercher"
            className="pl-9"
            value={search}
            onChange={(e) => filter(setSearch)(e.target.value)}
          />
        </div>
        <Select value={role} onValueChange={(v) => filter(setRole)(v as AssignableRole | typeof ALL)}>
          <SelectTrigger className="w-48" aria-label="Filtrer par rôle"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les rôles</SelectItem>
            {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => filter(setStatus)(v as MemberStatus | typeof ALL)}>
          <SelectTrigger className="w-44" aria-label="Filtrer par statut"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les statuts</SelectItem>
            {Object.entries(MEMBER_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <QueryError query={query} />
      <MembersTable members={data?.data} isLoading={isLoading} onChangeRole={setRoleTarget} onDelete={setDeleteTarget} />

      {data && data.total > 0 && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Précédent</Button>
          <span>Page {page}/{data.totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
        </div>
      )}

      <ChangeRoleDialog member={roleTarget} onOpenChange={(o) => { if (!o) setRoleTarget(null); }} />
      <DeleteMemberDialog member={deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }} />
    </div>
  );
}
